import { Link } from '@tanstack/react-router';
import { T, Var } from 'gt-react';
import {
  ChatTextIcon,
  FileTextIcon,
  InvoiceIcon,
  SparkleIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useArCollectionThreadEvidence } from '@/api/rpc/hooks/accounts-receivable/useArCollectionThreads';
import { useArInvoice } from '@/api/rpc/hooks/accounts-receivable/useArInvoices';
import { InvoicePickerCombobox } from './InvoicePickerCombobox';
import {
  contributingAdviceIds,
  deriveShortPayFigures,
  receivedPlusPromised,
} from './threadMoneyFigures';
import {
  DetailLine,
  ResolvedBadge,
  Stat,
  formatDate,
  formatMoney,
  readString,
} from './summaryCardPrimitives';

/**
 * Money + context strip shown at the top of a short-pay collection thread.
 *
 * Figures are derived by `deriveShortPayFigures` from the live invoice plus the
 * thread's remittance evidence — NOT read straight out of
 * `metadata.shortPayAmount`, whose meaning differs between the posted-payment
 * and remittance detection paths (see `threadMoneyFigures.ts`). A short pay that
 * originates from a remittance advice that hasn't cleared yet is called out
 * explicitly, because a collector otherwise sees an invoice with $0.00 received
 * and no explanation of where the short pay came from.
 */

interface Props {
  threadId: string;
  invoiceId: string | null;
  customerId: string;
  /** Persist a change to the thread's linked invoice. */
  onInvoiceChange: (invoiceId: string | null) => void;
  invoiceUpdating: boolean;
  metadata: Record<string, unknown>;
  onAddNote: () => void;
  /** Open the inline email composer in the thread view (no seeded prompt). */
  onDraftEmail: () => void;
  isResolved: boolean;
}

export function ShortPaySummaryCard({
  threadId,
  invoiceId,
  customerId,
  onInvoiceChange,
  invoiceUpdating,
  metadata,
  onAddNote,
  onDraftEmail,
  isResolved,
}: Props) {
  const { evidence } = useArCollectionThreadEvidence(threadId);
  // The linked invoice is the freshest view of the money and the only source
  // for manually-created threads, which carry no auto-detection metadata.
  const { invoice } = useArInvoice(invoiceId ?? undefined);

  const figures = deriveShortPayFigures({
    metadata,
    invoice: invoice
      ? {
          netAmount: invoice.netAmount,
          totalPaid: invoice.totalPaid,
          balanceDue: invoice.balanceDue,
        }
      : null,
    invoiceId,
    remittanceAdvices: evidence.remittanceAdvices,
    remittanceLineItems: evidence.remittanceLineItems,
    paymentApplications: evidence.paymentApplications,
  });

  const lastApplication = formatDate(readString(metadata.lastApplicationDate));
  const autoDetected = metadata.autoDetected === true;
  const fromRemittance = figures.basis === 'remittance_pending';

  // If we have none of the money signals, this card has nothing to say.
  if (
    figures.invoiceTotal == null &&
    figures.postedPaid == null &&
    figures.outstanding == null &&
    figures.pendingRemittance == null
  ) {
    return null;
  }

  // Newest receipt, used for the "Most recent payment" detail line. Receipts
  // come back ordered by transactionDate desc from the backend, but we sort
  // defensively in case that changes.
  const latestPayment = [...evidence.receipts].sort((a, b) => {
    const aTime = a.transactionDate ? new Date(a.transactionDate).getTime() : 0;
    const bTime = b.transactionDate ? new Date(b.transactionDate).getTime() : 0;
    return bTime - aTime;
  })[0];

  // Which remittance produced the pending intent, so the collector can open it.
  // Prefer an advice that actually contributed a line for this invoice; fall
  // back to the advice id the detection service stamped on the thread.
  const adviceIds = contributingAdviceIds(
    evidence.remittanceLineItems,
    invoiceId,
  );
  const metadataAdviceId = readString(metadata.remittanceAdviceId);
  const sourceRemittance =
    [...evidence.remittanceAdvices]
      .filter((r) => adviceIds.has(r.id))
      .sort((a, b) => {
        const aTime = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
        const bTime = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
        return bTime - aTime;
      })[0] ??
    evidence.remittanceAdvices.find((r) => r.id === metadataAdviceId) ??
    null;
  const remittanceDate = formatDate(
    sourceRemittance?.statedPaymentDate ?? sourceRemittance?.receivedAt ?? null,
  );
  const remittanceChannel =
    sourceRemittance?.statedPaymentMethod ?? sourceRemittance?.channel ?? null;
  const remittanceRef = sourceRemittance?.statedReferenceNo ?? null;

  const receivedValue = receivedPlusPromised(
    figures.postedPaid,
    figures.pendingRemittance,
  );

  // A shortfall of zero-or-less is a real answer ("the remittance covers it"),
  // but "$0.00" under a "Short" label reads as "nothing is wrong". Say it in
  // words instead — and never print $0.00 for a value we simply don't know.
  const shortfallCovered =
    figures.shortfall != null && Number(figures.shortfall) <= 0.005;
  const shortValue: React.ReactNode =
    figures.shortfall == null ? (
      <span className="text-base font-medium text-muted-foreground">
        <T context="stat value, short pay amount cannot be computed">
          Not yet known
        </T>
      </span>
    ) : shortfallCovered ? (
      <span className="text-base font-medium text-muted-foreground">
        <T context="stat value, remittance covers the whole balance">
          None expected
        </T>
      </span>
    ) : (
      formatMoney(figures.shortfall)
    );

  return (
    <div className="rounded-lg border border-stone-400 dark:border-stone-600 bg-card overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b bg-stone-100 dark:bg-stone-800">
        <span className="text-lg font-semibold text-foreground">
          <T context="section title, short pay summary">Short pay summary</T>
        </span>
        {isResolved && <ResolvedBadge />}
        {fromRemittance && (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-500 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
            <FileTextIcon className="h-3 w-3" weight="fill" />
            <T context="badge, short pay comes from a remittance advice that has not posted">
              From remittance advice · not yet applied
            </T>
          </span>
        )}
        {lastApplication && (
          <span className="text-xs text-muted-foreground ml-auto">
            <T context="last payment application date">
              Last applied <Var>{lastApplication}</Var>
            </T>
          </span>
        )}
      </div>

      <div
        className={`grid divide-x ${fromRemittance ? 'grid-cols-4' : 'grid-cols-3'}`}
      >
        <Stat
          label={
            <T context="stat label, original invoice amount">Invoice total</T>
          }
          value={formatMoney(figures.invoiceTotal)}
        />
        {fromRemittance ? (
          <>
            <Stat
              label={
                <T context="stat label, cash applied to the invoice so far">
                  Applied so far
                </T>
              }
              value={formatMoney(figures.postedPaid)}
              sub={
                // A zero here is real, not missing data — spell that out so it
                // can't be misread as "the short pay is zero".
                figures.postedPaid != null &&
                Number(figures.postedPaid) <= 0.005 ? (
                  <T context="stat sub, no cash has cleared against this invoice yet">
                    Nothing cleared yet
                  </T>
                ) : latestPayment?.paymentType &&
                  latestPayment.transactionDate ? (
                  `${latestPayment.paymentType} · ${formatDate(latestPayment.transactionDate)}`
                ) : (
                  (latestPayment?.paymentType ?? null)
                )
              }
            />
            <Stat
              label={
                <T context="stat label, amount the remittance advice states for this invoice">
                  Remittance says
                </T>
              }
              value={formatMoney(
                figures.statedForInvoice ?? figures.pendingRemittance,
              )}
              tone="pending"
              sub={
                <T context="stat sub, remittance amount has not been applied yet">
                  <Var>{formatMoney(figures.pendingRemittance)}</Var> not yet
                  applied
                </T>
              }
            />
            <Stat
              label={
                <T context="stat label, short pay remaining once the remittance posts">
                  Short after remittance
                </T>
              }
              value={shortValue}
              tone="warn"
              sub={
                <T context="stat sub, invoice balance before the remittance posts">
                  <Var>{formatMoney(figures.outstanding)}</Var> owed today
                </T>
              }
            />
          </>
        ) : (
          <>
            <Stat
              label={<T context="stat label, amount received">Received</T>}
              value={formatMoney(receivedValue)}
              sub={
                latestPayment?.paymentType && latestPayment.transactionDate
                  ? `${latestPayment.paymentType} · ${formatDate(latestPayment.transactionDate)}`
                  : (latestPayment?.paymentType ?? null)
              }
            />
            <Stat
              label={<T context="stat label, amount short">Short</T>}
              value={shortValue}
              tone="warn"
            />
          </>
        )}
      </div>

      {fromRemittance && (
        <div className="border-t border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-900 dark:bg-amber-950/30">
          <div className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            <T context="callout title, short pay is based on an unposted remittance advice">
              This short pay comes from a remittance advice, not a posted
              payment.
            </T>
          </div>
          <div className="mt-1 text-sm leading-snug text-amber-900/90 dark:text-amber-200/90">
            {figures.shortfall != null && !shortfallCovered ? (
              <T context="callout body, remittance intent versus invoice balance">
                The customer says they are paying{' '}
                <Var>{formatMoney(figures.pendingRemittance)}</Var> against the{' '}
                <Var>{formatMoney(figures.outstanding)}</Var> still owed on this
                invoice — <Var>{formatMoney(figures.shortfall)}</Var> short. No
                cash has been applied for it yet.
              </T>
            ) : (
              <T context="callout body, remittance intent with no computable shortfall">
                The customer says they are paying{' '}
                <Var>{formatMoney(figures.pendingRemittance)}</Var> against this
                invoice. No cash has been applied for it yet.
              </T>
            )}
          </div>
          {figures.deductionTotal != null &&
            Number(figures.deductionTotal) > 0.005 && (
              <div className="mt-1 text-sm leading-snug text-amber-900/90 dark:text-amber-200/90">
                <T context="callout body, remittance withheld a deduction on this invoice">
                  Includes <Var>{formatMoney(figures.deductionTotal)}</Var> the
                  customer withheld as a deduction.
                </T>
              </div>
            )}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-amber-900/80 dark:text-amber-200/80">
            {sourceRemittance ? (
              <Link
                to="/accounts-receivable/remittances/$remittanceId"
                params={{ remittanceId: sourceRemittance.id }}
                className="font-medium underline underline-offset-2 hover:no-underline"
              >
                {remittanceRef ? (
                  <T context="link, open the source remittance advice by reference">
                    Remittance <Var>{remittanceRef}</Var>
                  </T>
                ) : (
                  <T context="link, open the source remittance advice">
                    View remittance advice
                  </T>
                )}
              </Link>
            ) : (
              <span>
                <T context="detail, source remittance advice is not linked as evidence">
                  Source remittance not linked to this thread.
                </T>
              </span>
            )}
            {remittanceDate && (
              <span>
                <T context="detail, remittance advice date">
                  Dated <Var>{remittanceDate}</Var>
                </T>
              </span>
            )}
            {remittanceChannel && <span>{remittanceChannel}</span>}
          </div>
        </div>
      )}

      <div className="border-t px-4 py-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          <T context="details list label">Details</T>
        </div>
        <ul className="flex flex-col gap-1.5">
          {autoDetected && (
            <DetailLine
              text={
                <T context="detail, auto-detected by collections agent">
                  Auto-opened by Gently when the short pay was detected.
                </T>
              }
            />
          )}
          {latestPayment?.referenceNumber &&
            (latestPayment.paymentType ? (
              <DetailLine
                text={
                  <T context="detail, most recent payment with method">
                    Latest payment:{' '}
                    <Var>{formatMoney(latestPayment.amount)}</Var> via{' '}
                    <Var>{latestPayment.paymentType}</Var> (ref{' '}
                    <Var>{latestPayment.referenceNumber}</Var>).
                  </T>
                }
              />
            ) : (
              <DetailLine
                text={
                  <T context="detail, most recent payment">
                    Latest payment:{' '}
                    <Var>{formatMoney(latestPayment.amount)}</Var> (ref{' '}
                    <Var>{latestPayment.referenceNumber}</Var>).
                  </T>
                }
              />
            ))}
          {evidence.paymentApplications.length > 0 && (
            <DetailLine
              text={
                <T context="detail, payment applications count">
                  <Var>{evidence.paymentApplications.length}</Var> payment
                  application(s) recorded against this invoice.
                </T>
              }
            />
          )}
          {figures.outstanding != null && (
            <DetailLine
              text={
                <T context="detail, unapplied balance">
                  <Var>{formatMoney(figures.outstanding)}</Var> remains
                  unapplied on this invoice.
                </T>
              }
            />
          )}
        </ul>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t bg-muted/30 px-4 py-2">
        {!isResolved && (
          <Button size="sm" variant="outline" onClick={onDraftEmail}>
            <SparkleIcon className="h-4 w-4 mr-1" />
            <T context="action, draft collection email with ai">Draft email</T>
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={onAddNote}>
          <ChatTextIcon className="h-4 w-4 mr-1" />
          <T context="action, add a note">Add note</T>
        </Button>
        {invoiceId && (
          <Button size="sm" variant="ghost" asChild>
            <Link
              to="/accounts-receivable/invoices/$arInvoiceId"
              params={{ arInvoiceId: invoiceId }}
            >
              <InvoiceIcon className="h-4 w-4 mr-1" />
              <T context="action, view linked invoice">View invoice</T>
            </Link>
          </Button>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">
            <T context="label, update the invoice linked to this thread">
              Update invoice
            </T>
          </span>
          <InvoicePickerCombobox
            customerId={customerId}
            value={invoiceId}
            onChange={onInvoiceChange}
            disabled={invoiceUpdating || isResolved}
            compact
          />
        </div>
      </div>
    </div>
  );
}
